import { useDispatch } from "react-redux";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importOpenSidebar } from "../../../../../../redux/actions/openSidebar";
import { dt } from "../../../../../../utils/documentsI18n";
import { getUnmatchErrorAlert, normalizeUnmatchId } from "../utils/unmatchInput";
import { useBulkUnmatch } from "./useBulkUnmatch";

/**
 * Deshace TODOS los matchs de un registro (movimiento o documento) vía el job
 * async `bulkUnmatch`. El deshacer de UN match sigue en MatchRow con
 * generateMultipleUnMatch (fail-fast, UI inmediata).
 *
 * @returns {{ undoAll: () => Promise<void>, isUndoing: boolean, job, percent, cancel, retry, reset }}
 */
export const useUndoAllMatches = ({
  editData,
  isDocument,
  companyId,
  matches,
}) => {
  const dispatch = useDispatch();

  const bulk = useBulkUnmatch({
    companyId,
    onSettled: (finalJob) => {
      if (finalJob?.status === "COMPLETED") {
        dispatch(importOpenSidebar({ show: false }));
      }
    },
  });

  const isUndoing = bulk.launching || bulk.inFlight;

  const undoAll = async () => {
    if (isUndoing) return;

    try {
      if (!matches?.length) {
        dispatch(
          importAlertState({
            content: dt("reconcile.unmatchIdentifyError"),
            state: "error",
            duration: 4,
          }),
        );
        return;
      }

      const targetId = normalizeUnmatchId(editData?._id || editData?.id);
      if (!targetId) {
        dispatch(
          importAlertState({
            content: dt("reconcile.unmatchIdentifyError"),
            state: "error",
            duration: 4,
          }),
        );
        return;
      }

      await bulk.launch({
        type: bulkUnmatchTargetTypeFromEditData(editData, isDocument),
        ids: [targetId],
        deleteMatchCreatedObligations: true,
      });
    } catch (e) {
      console.error("Error al deshacer todos los matchs:", e);
      dispatch(
        importAlertState({
          ...getUnmatchErrorAlert(e, dt("reconcile.unmatchError")),
        }),
      );
    }
  };

  return {
    undoAll,
    isUndoing,
    job: bulk.job,
    percent: bulk.percent,
    cancel: bulk.cancel,
    retry: bulk.retry,
    reset: bulk.reset,
  };
};

export const bulkUnmatchTargetTypeFromEditData = (editData, isDocument) => {
  if (!isDocument) return "TRANSACTION";
  const type = String(editData?.documentType || "").toUpperCase();
  if (type === "OBLIGATION") return "OBLIGATION";
  if (type === "CREDIT") return "CREDIT";
  return "TAX_DOCUMENT";
};
